import {
  createRef,
  forwardRef,
  Fragment,
  useEffect,
  useState,
  useRef,
} from 'react';
import { PropTypes } from 'prop-types';
import classnames from 'classnames';
import { default as ReactSelect, components } from 'react-select';

import { optionShape } from 'helpers/shared_types';
import { useOutsideClick } from 'helpers/clickOutside';

import './select.scss';

const Menu = ({
  selectProps: { onMenuInputFocus, onOtherInput },
  ...props
}) => {
  const handleKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      onOtherInput(e.target.value);
    }
  };

  return (
    <Fragment>
      <components.Menu {...props}>
        <div>
          {props.children}
          <input
            className="other-input"
            autoCorrect="off"
            autoComplete="off"
            spellCheck="false"
            type="text"
            placeholder="Other"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.target.focus();
            }}
            onFocus={onMenuInputFocus}
            onKeyDown={handleKeyDown}
          />
        </div>
      </components.Menu>
    </Fragment>
  );
};

Menu.propTypes = {
  selectProps: {
    onMenuInputFocus: PropTypes.func.isRequired,
    onOtherInput: PropTypes.func.isRequired,
  },
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

function Select({
  className,
  label,
  value,
  options = [],
  valid,
  validationText = 'This question is required!',
  selectRef = createRef(),
  isMulti,
  isDisabled,
  onChange,
  withOther,
  hidden,
}) {
  const wrapperRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [defaultValue, setDefaultValue] = useState(
    value ? value : options.find((el) => el.default),
  );

  useEffect(() => setDefaultValue(value), [value]);
  useOutsideClick(wrapperRef, () => setIsFocused(false));

  const handleOtherInput = (value) => {
    const otherOption = { id: value, label: value, value };

    onChange(otherOption);
    setDefaultValue(otherOption);
    setIsFocused(false);
  };

  const handleOnChange = (value) => {
    setIsFocused(false);
    onChange(value);
  };

  const wrapperClasses = classnames('input-group', {
    error: !valid,
    'multi-select': isMulti,
    'single-select': !isMulti,
    hidden: hidden,
  });

  const selectProps = {
    isMulti,
    isDisabled,
    defaultValue,
    defaultOptions: true,
    placeholder: '',
    ref: selectRef,
    className,
    classNamePrefix: 'select',
    value: defaultValue,
    onChange,
    options,
  };

  const withOtherProps = withOther
    ? {
        components: { Menu },
        onMenuInputFocus: () => setIsFocused(true),
        onOtherInput: handleOtherInput,
        menuIsOpen: isFocused || undefined,
        isFocused: isFocused || undefined,
        onChange: handleOnChange,
      }
    : {};

  return (
    <div className={wrapperClasses} ref={wrapperRef}>
      <label>{label}</label>

      <ReactSelect {...selectProps} {...withOtherProps} />
      {!valid && <span className="error">{validationText}</span>}
    </div>
  );
}

Select.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  value: optionShape,
  options: PropTypes.arrayOf(optionShape),
  valid: PropTypes.bool,
  validationText: PropTypes.string,
  selectRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  isMulti: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func,
  withOther: PropTypes.bool,
};

export default forwardRef(function reactSelect(props, ref) {
  return <Select selectRef={ref} {...props} />;
});
